import { IEvent } from 'fabric/fabric-impl';
import cloneDeep from 'lodash.clonedeep';
import { PutEffect, SelectEffect, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';

import {
  ADD_CAMERA,
  SET_CAMERA_EASE_TYPE,
  SET_CAMERA_TO_MATCH_EDGE,
  SET_CAMERA_TO_MATCH_VIEW,
  SET_VIEW_TO_CAMERA,
  SET_VIEW_TO_ELEMENT,
  SetCameraEasingTypeAction,
  SetCameraToMatchEdgeAction,
  SetCameraToMatchViewAction,
  SetViewToCameraAction,
  SetViewToElementAction
} from 'js/actionCreators/cameraActions';
import {
  PIN_ACTIVE_CAMERAS,
  PIN_CAMERAS,
  TOGGLE_CAMERA_PINS,
  UNPIN_ACTIVE_CAMERAS,
  UNPIN_CAMERAS,
  updateScribe
} from 'js/actionCreators/scribeActions';
import { CAMERA_EASING_NONE } from 'js/config/consts';
import { getEditorCanvas } from 'js/editor/EditorCanvas/EditorCanvas';
import { getAdjustedNewCameraRect } from 'js/editor/EditorCanvas/helpers/getNewCameraRect';
import ScribeElementModel from 'js/models/ScribeElementModel';
import { ExistingScribeModel, ScribeCameraElement } from 'js/types';
import { CLEAR_REPLACE_IMAGE, ClearReplaceImageAction } from 'js/actionCreators/imagesActions';

import { getViewportData } from './sagaHelpers/getViewportData';
import { getScribeById } from './selectors';
import { addCamera } from './cameraSagas/addCamera';
import {
  toggleActiveSceneCameraPins,
  updateCamerasPinned,
  updateCamerasPinnedOnActiveElements
} from './cameraSagas/updateCamerasPinned';

function* setCameraToMatchEdge({ edge, position, value, elementIds, scribeId }: SetCameraToMatchEdgeAction) {
  const project: ExistingScribeModel | undefined = yield select(getScribeById, scribeId);
  if (!project) return;
  const clonedProject = cloneDeep(project);
  const targetElements = clonedProject.elements.filter(
    element => elementIds.includes(element.id) && element.type === 'Camera'
  );
  targetElements.forEach(element => {
    if (element.type === 'Camera') {
      const canvasSize = getViewportData(clonedProject.canvasSize);
      if (position) {
        element.x = position.x;
        element.y = position.y;
      }
      element.scale = edge === 'x' ? canvasSize.width / value : canvasSize.height / value;
    }
  });

  yield put(updateScribe(clonedProject));
}

function applyViewToCamera(cameraElement: ScribeCameraElement, viewportData: { width: number; height: number }) {
  const editorCanvas = getEditorCanvas();
  if (editorCanvas && cameraElement) {
    editorCanvas.setViewToCamera(cameraElement, viewportData);
  }
}

function* setCameraEaseType({ easeType, scribeId, elementIds }: SetCameraEasingTypeAction) {
  const project: ExistingScribeModel | undefined = yield select(getScribeById, scribeId);
  if (!project || !elementIds) return;

  const clonedProject = cloneDeep(project);

  const targetElements = clonedProject.elements.filter(
    element => elementIds.includes(element.id) && element.type === 'Camera'
  );
  targetElements.forEach(element => {
    const cameraElement = element as ScribeCameraElement;
    cameraElement.easingType = easeType;
    if (easeType === CAMERA_EASING_NONE) cameraElement.animationTime = 0;
    else if (easeType !== undefined && cameraElement.animationTime === 0) cameraElement.animationTime = 1;
  });
  yield put(updateScribe(clonedProject));
}

function* setViewToCamera({ cameraElement, projectId }: SetViewToCameraAction) {
  const project: ExistingScribeModel | undefined = yield select(getScribeById, projectId);
  const viewportData = getViewportData(project?.canvasSize);
  yield call(applyViewToCamera, cameraElement, viewportData);
}

function applyViewToElement(element: ScribeElementModel) {
  const editorCanvas = getEditorCanvas();
  if (editorCanvas && element) {
    editorCanvas.setViewToActiveObject();
  }
}

function* setViewToElement({ element }: SetViewToElementAction) {
  yield call(applyViewToElement, element);
}

function* setCameraToMatchView({
  cameraId,
  projectId
}: SetCameraToMatchViewAction): Generator<SelectEffect | PutEffect, void, ExistingScribeModel | undefined> {
  const project = yield select(getScribeById, projectId);
  if (!project) return;

  const clonedProject = cloneDeep(project);
  const cameraElement = clonedProject.elements.find((el): el is ScribeCameraElement => el.id === cameraId);
  if (!cameraElement) return;

  const editorCanvas = getEditorCanvas();
  if (!editorCanvas) return;

  const viewportRectAndCenter = editorCanvas.getViewportRectAndCenter();
  if (!viewportRectAndCenter) return;

  const { adjustedCameraRectangle, scale } = getAdjustedNewCameraRect(
    viewportRectAndCenter,
    editorCanvas.canvasSize,
    editorCanvas.canvasBounds
  );

  cameraElement.scale = scale;
  cameraElement.x = adjustedCameraRectangle.x;
  cameraElement.y = adjustedCameraRectangle.y;

  yield put(updateScribe(clonedProject));
}

function handleClearReplaceImage({ focusActiveElement }: ClearReplaceImageAction) {
  const editor = getEditorCanvas();

  if (editor && focusActiveElement) {
    const handleImageAddedToCanvas = (event: IEvent) => {
      editor.off('object:added', handleImageAddedToCanvas);

      if (!event.target) return;
      const { target } = event;
      const fabBounds = target.getBoundingRect(true, false);

      editor.setViewToObject(fabBounds.width, fabBounds.height, fabBounds.left, fabBounds.top);
    };
    editor.on('object:added', handleImageAddedToCanvas);
  }
}

export default function* cameraSagas() {
  yield takeEvery(ADD_CAMERA, addCamera);
  yield takeEvery(SET_CAMERA_TO_MATCH_EDGE, setCameraToMatchEdge);
  yield takeEvery(SET_VIEW_TO_CAMERA, setViewToCamera);
  yield takeEvery(SET_CAMERA_EASE_TYPE, setCameraEaseType);
  yield takeEvery(SET_VIEW_TO_ELEMENT, setViewToElement);
  yield takeEvery(SET_CAMERA_TO_MATCH_VIEW, setCameraToMatchView);
  yield takeEvery(CLEAR_REPLACE_IMAGE, handleClearReplaceImage);
  yield takeLatest([PIN_CAMERAS, UNPIN_CAMERAS], updateCamerasPinned);
  yield takeLatest([PIN_ACTIVE_CAMERAS, UNPIN_ACTIVE_CAMERAS], updateCamerasPinnedOnActiveElements);
  yield takeLatest(TOGGLE_CAMERA_PINS, toggleActiveSceneCameraPins);
}
